import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import type { WS } from '@trinity/chat';
import { ChatQueryStatus, SocketNamespace, QuerySocketEvent  } from '@trinity/chat';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useChatQuerySocket } from '@trinity/chat-components/websocket';
import { createLogger } from '@trinity/chat-components/websocket/utils';

const POLL_INITIAL_DELAY_MS = 2000;
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_RETRIES = 5;

type Props = {
  children: React.ReactNode;
  queryIdentifier: string;
  queryStatus: ChatQueryStatus;
};

export const QueryFollowupsContainer = (props: Props) => {

  const logger = useMemo(() => createLogger({
    namespace: SocketNamespace.Queries,
  }), []);

  const enabled = useMemo(() => {
    const result = props.queryStatus === ChatQueryStatus.Complete;
    logger.log(`[FollowupsContainer] enabled check — queryStatus: ${props.queryStatus}, enabled: ${result}`);
    return result;
  }, [props.queryStatus, logger]);

  const dispatch = useContext(ChatStateDispatchContext);
  const apiClient = useChatApiClient();
  const socket = useChatQuerySocket(props.queryIdentifier, enabled);

  const handleFollowupsAvailable = useCallback((data: WS.QueryFollowupsAvailable.Data) => {
    logger.log(`[FollowupsContainer] received ${QuerySocketEvent.QueryFollowupsAvailable}`);
    dispatch({
      type: 'query-followups-available',
      payload: {
        queryIdentifier: data.queryIdentifier,
        items: data.items,
      },
    });
  }, [dispatch, logger]);

  useEffect(() => {
    logger.log(`[FollowupsContainer] socket effect — socket: ${socket ? 'connected' : 'null'}, enabled: ${enabled}, queryIdentifier: ${props.queryIdentifier}`);
    if (!socket) return;

    logger.log(`[FollowupsContainer] registering listener for ${QuerySocketEvent.QueryFollowupsAvailable}`);
    socket.on(QuerySocketEvent.QueryFollowupsAvailable, handleFollowupsAvailable);

    return () => {
      logger.log(`[FollowupsContainer] removing listener for ${QuerySocketEvent.QueryFollowupsAvailable}`);
      socket.off(QuerySocketEvent.QueryFollowupsAvailable, handleFollowupsAvailable);
    };
  }, [handleFollowupsAvailable, socket, enabled, props.queryIdentifier, logger]);

  // Polling fallback: if the socket misses the QueryFollowupsAvailable event (race between
  // socket connection setup and the background job completing), poll the API to catch up.
  const retriesRef = useRef(0);
  useEffect(() => {
    if (!enabled) return;

    retriesRef.current = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = () => {
      apiClient.chat
        .getPendingQuery({ queryIdentifier: props.queryIdentifier })
        .then((res) => {
          if (!res.query) return;

          if (res.query.status === ChatQueryStatus.FollowupsAvailable && res.query.suggestedFollowups.length > 0) {
            logger.log(`[FollowupsContainer] poll found followups for ${props.queryIdentifier}`);
            dispatch({
              type: 'query-followups-available',
              payload: {
                queryIdentifier: res.query.identifier,
                items: res.query.suggestedFollowups,
              },
            });
            return;
          }

          if (res.query.status === ChatQueryStatus.Complete && retriesRef.current < POLL_MAX_RETRIES) {
            retriesRef.current += 1;
            timer = setTimeout(poll, POLL_INTERVAL_MS);
          }
        })
        .catch(() => {
          if (retriesRef.current < POLL_MAX_RETRIES) {
            retriesRef.current += 1;
            timer = setTimeout(poll, POLL_INTERVAL_MS);
          }
        });
    };

    timer = setTimeout(poll, POLL_INITIAL_DELAY_MS);

    return () => {
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [enabled, props.queryIdentifier, apiClient, dispatch, logger]);

  return (
    <>
      {props.children}
    </>
  );
};